import { useCallback, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { Button, Loading } from '@carbon/react';
import { Add, WarningFilled } from '@carbon/react/icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { format0 } from 'utils/number';
import { wellStatusLabel } from 'utils/well-status';
import { colorRulesAtom, datasetFiltersAtom } from './atoms';
import { AttributeFilterModal } from './attribute-filter-modal';
import { ColorRuleCard, FilterCard } from './filter-cards';
import { useColorRuleStatuses, useFilterOptions } from './hooks';
import { ApiEnvelopeError } from 'lib/evaluation-case/api-envelope';
import {
  attributeLabel,
  mergeByAttribute,
  visibleColorRules,
  withSelectedValuesVisible,
} from './utils';
import type {
  AppliedFilter,
  AttributeFilterApply,
  ColorRule,
  ColorTarget,
  DatasetName,
  FilterAttributeOption,
  FilterCatalog,
  FilterOption,
  FilterSelection,
} from './types';
import styles from './dataset-filter-accordion.module.scss';

/**
 * Display label per dataset for the accordion title (mock §4). The `wells` dataset
 * filters the whole property population (wells + well-less LTD), so it's surfaced
 * as "Properties" per the agreed vocabulary — the map/filter key stays `wells`.
 */
const DATASET_LABELS: Record<DatasetName, string> = {
  assets: 'Assets',
  wells: 'Properties',
  units: 'Units',
};

/**
 * Attributes whose `filter_options` values are STORED tokens rather than human
 * text, and how to display one. The modal is domain-agnostic, so the mapping lives
 * here (the domain-aware caller) and is handed down per attribute.
 *
 * Only well status qualifies today — the BE emits the facet verbatim (`shut_in`,
 * `expired_permit`, `LTD`), and the value the user picks is sent back unchanged.
 * Every other categorical facet (operator, formation, basin, …) is already
 * readable and must stay verbatim, so it is deliberately absent here.
 */
const VALUE_LABELS: Record<string, ((value: string) => string) | undefined> = {
  wellStatus: wellStatusLabel,
};

/**
 * Display text from the labels the backend sends with a categorical option, such
 * as a DCA forecast's business id for its `volumeSourceId`.
 *
 * @param {FilterOption} option - The attribute's filter_options entry.
 * @return {Function|undefined} The value-to-label lookup, or undefined when the option has no labels.
 */
function catalogValueLabel(
  option: FilterOption
): ((value: string) => string) | undefined {
  if (option.type !== 'categorical' || !option.labels) {
    return undefined;
  }
  const labels = option.labels;
  return (value) => labels[value] ?? value;
}

/** Color-rule paint targets offered in the modal's "Apply to" pane (P2: Map only). */
const COLOR_TARGETS: { id: string; label: string }[] = [
  { id: 'map', label: 'Map' },
];

/** Tooltip shown on a disabled (eval-scoped) attribute row in the modal. */
const EVAL_SCOPED_HINT = 'Available after an eval-calc run.';

/**
 * Whether an eval-scoped attribute (BE `requiresEvalCalc`) must be disabled in the
 * modal. Eval-scoped values only carry meaning from an eval-calc run, and the read
 * endpoints `422` on predicates over them without a `calculationId` — so they're
 * disabled in PACKAGE mode (no calc) and ENABLED in CALC mode (the URL carries an
 * `:evalId`, sent as `calculationId`, where the BE resolves real values + accepts
 * the predicates). The modal stays domain-agnostic; it only sees the derived
 * `disabled`.
 *
 * @param {FilterOption} option - The attribute's catalog option.
 * @param {boolean} hasCalc - Whether the page is in calc mode (a `calculationId` is set).
 * @return {boolean} True when the attribute can't be filtered/colored (package mode).
 */
function isEvalScopedDisabled(option: FilterOption, hasCalc: boolean): boolean {
  return Boolean(option.requiresEvalCalc) && !hasCalc;
}

/** Which section opened the modal + the attribute it should focus (null = closed). */
interface ModalState {
  mode: 'filter' | 'color';
  focus: string | null;
}

/**
 * Narrows the modal's opaque `applyTo` ids to the known `ColorTarget` union, keeping
 * only valid targets. Defaults to `['map']` — the modal's Apply gating already
 * guarantees a color selection carries ≥1 target, so this is belt-and-suspenders.
 *
 * @param {string[]} [applyTo] - The opaque target ids the modal emitted.
 * @return {ColorTarget[]} The valid color targets.
 */
function narrowApplyTo(applyTo?: string[]): ColorTarget[] {
  return (applyTo ?? ['map']).filter((id): id is ColorTarget => id === 'map');
}

/**
 * One dataset section's action button (Add Filter / Color by Attribute), driven by
 * the `filter_options` catalog state so the recovery lives in the button itself —
 * no notification chrome in the narrow panel:
 *   - `loading` → a disabled "Loading…" label with a small inline spinner;
 *   - `error` → a compact red (`danger--tertiary`) Retry with a warning icon; its
 *     `onRetry` refetches the shared catalog (one fetch backs all three accordions,
 *     so any Retry heals all). It replaces the action, so a doomed empty modal can't
 *     open;
 *   - `unavailable` → the BE called the failure permanent (`retryable: false`), so the same
 *     red marker appears DISABLED with no Retry: the catalog cannot load for this
 *     calculation until it is re-run, and a button here would only fail again (INS-12433);
 *   - `ready` → the live button that opens the modal.
 *
 * @param {object} props - The catalog `status`, the ready-state `label`, the `onOpen` / `onRetry` handlers, and the `unavailableReason` tooltip.
 * @return {JSX.Element} The action button for the current catalog state.
 */
function SectionActionButton({
  status,
  label,
  onOpen,
  onRetry,
  unavailableReason,
}: {
  status: 'loading' | 'error' | 'unavailable' | 'ready';
  label: string;
  onOpen: () => void;
  onRetry: () => void;
  /** The BE's explanation, surfaced as the tooltip on the `unavailable` state. */
  unavailableReason?: string;
}) {
  if (status === 'loading') {
    return (
      <Button className={styles.addButton} kind="tertiary" size="sm" disabled>
        <span className={styles.loadingLabel}>
          <Loading small withOverlay={false} description="Loading options" />
          Loading…
        </span>
      </Button>
    );
  }
  if (status === 'error') {
    return (
      <Button
        className={styles.addButton}
        kind="danger--tertiary"
        size="sm"
        renderIcon={WarningFilled}
        // Visible "Retry" + failure context as the accessible name (contains the
        // visible label, per WCAG label-in-name).
        aria-label="Retry loading filter options"
        onClick={onRetry}
      >
        Retry
      </Button>
    );
  }
  // INS-12433 — the BE said this failure is permanent (`retryable: false`), so there is no
  // Retry to offer: the options cannot load for this calculation until it is re-run. Shown
  // as a disabled marker rather than a button that would only fail again, with the BE's
  // message (which names what is missing and how to fix it) as the tooltip.
  if (status === 'unavailable') {
    return (
      <Button
        className={styles.addButton}
        kind="danger--tertiary"
        size="sm"
        renderIcon={WarningFilled}
        disabled
        title={unavailableReason}
        aria-label="Filter options unavailable for this evaluation"
      >
        Unavailable
      </Button>
    );
  }
  return (
    <Button
      className={styles.addButton}
      kind="tertiary"
      size="sm"
      renderIcon={Add}
      onClick={onOpen}
    >
      {label}
    </Button>
  );
}

interface DatasetFilterAccordionProps {
  /** Which dataset this accordion edits. */
  dataset: DatasetName;
  /** The package to scope the catalog + color resolution to (from the page container). */
  packageId: number;
  /** The eval-calc id (calc mode) or null (package mode) — drives the eval-scoped gate. */
  calculationId: number | null;
  /** Best-effort count for the title; omitted ⇒ no `(N)`. */
  count?: number;
}

/**
 * One dataset's accordion (Assets / Wells / Units), holding BOTH its Filters section
 * and its Color by Attribute section (mock §4c). Each section's button opens the
 * shared domain-agnostic `AttributeFilterModal` — filter mode or color mode — seeded
 * with this dataset's `filter_options` catalog and its current rules; on apply the
 * modal's `{ selections, removed }` is merged by (dataset, attribute) back onto the
 * matching atom (`datasetFiltersAtom` for filters, the per-dataset
 * `colorRulesAtom(dataset)` for color rules), so there's one card per attribute per
 * dataset. Edit pre-loads the modal on the clicked attribute; Remove drops the card;
 * a color rule's toggle flips its `enabled`. Each color card shows its `map_elements`
 * resolution status, observed via `useColorRuleStatuses` (which shares the layer's
 * cache keys — no second fetch). The title carries a best-effort `(N)` count when
 * supplied.
 *
 * @param {DatasetFilterAccordionProps} props - The dataset, page identity, and optional title count.
 * @return {JSX.Element} The dataset's accordion item.
 */
export function DatasetFilterAccordion({
  dataset,
  packageId,
  calculationId,
  count,
}: DatasetFilterAccordionProps) {
  const [allFilters, dispatchFilters] = useAtom(datasetFiltersAtom);
  // The per-dataset color-rule list (the atomFamily instance is stable per dataset).
  const [colorRules, dispatchColors] = useAtom(colorRulesAtom(dataset));
  // The filter_options catalog: its load state drives the section buttons (via
  // `SectionActionButton` — spinner while loading, a Retry on failure). The catalog
  // is staleTime: Infinity and the three dataset accordions share one fetch, so a
  // Retry from any heals all. The buttons only open the modal once the catalog is
  // ready, so the modal always mounts with a populated `attributes` (preserving its
  // seed-once invariant — no in-modal loading/late-arrival states needed).
  const {
    data: catalogs,
    isPending: catalogLoading,
    isError: catalogError,
    // INS-12433 — the error OBJECT, not just `isError`: a permanent failure carries
    // `retryable: false` and a message naming what is missing, and both are needed to
    // decide whether a Retry is worth offering at all.
    error: catalogErrorObject,
    refetch: refetchCatalog,
  } = useFilterOptions(packageId, calculationId);
  const catalogUnavailable =
    catalogErrorObject instanceof ApiEnvelopeError &&
    !catalogErrorObject.retryable;
  const catalogStatus = catalogError
    ? catalogUnavailable
      ? 'unavailable'
      : 'error'
    : catalogLoading
      ? 'loading'
      : 'ready';
  // Calc mode (the URL carries an `:evalId`) enables eval-scoped attributes; package
  // mode (no calc) disables them. Drives the per-attribute `disabled` below.
  const hasCalc = calculationId !== null;

  // The modal's open + mode + focus intent: `null` = closed; otherwise the section
  // (`filter` / `color`) and the attribute Edit landed on (`null` for Add).
  const [modal, setModal] = useState<ModalState | null>(null);

  // This dataset's applied filters (the shared list is keyed on (dataset, attribute)).
  const datasetFilters = useMemo(
    () => allFilters.filter((filter) => filter.dataset === dataset),
    [allFilters, dataset]
  );

  // The map-painting subset — the SAME filter the layers apply (enabled ∧ targeting
  // the map). Driving the color-resolution status off this (not the full list) means
  // the panel's calls share the layers' EXACT cache keys (one fetch, true parity) and
  // disabled rules — which paint nothing — aren't resolved.
  const mapColorRules = useMemo(
    () =>
      colorRules.filter((rule) => rule.enabled && rule.applyTo.includes('map')),
    [colorRules]
  );
  // Per-rule color-resolution status for the cards (shares the layers' map_elements
  // cache keys — no second fetch).
  const colorStatuses = useColorRuleStatuses(
    packageId,
    calculationId,
    mapColorRules
  );

  // Legend hiding (INS-10376 Bug 13): drop colors for values the active filters
  // narrowed out (see visibleColorRules), reading the fully-narrowed `colorBy` catalog
  // (color-by is not part of the cascade — it reflects ALL active filters). Display
  // -only — the stored rules keep every value. Skipped while the catalog loads so
  // colors don't blink away.
  //
  // Map/legend consistency: the map paints from the FULL rule set (mapColorRules)
  // while the legend renders this NARROWED set — but they can't diverge. The map
  // only renders features in the visibility set (useMapVisibility narrows each layer
  // to the filtered population), so a color whose value is filtered out has no
  // feature to paint; the legend hides that same color. With no filter, nothing is
  // narrowed and both show everything. So the painted colors are always a subset of
  // the legend's — never a color on the map that the legend omits.
  const legendColorRules = useMemo<ColorRule[]>(() => {
    if (!catalogs) {
      return colorRules;
    }
    return visibleColorRules(colorRules, catalogs.colorBy[dataset]);
  }, [colorRules, catalogs, dataset]);

  // The catalog feeding the modal picker (INS-10376 Bug 13): filter mode reads the
  // `cascade` (each attribute narrowed by the filters ABOVE it in insertion order);
  // color mode reads the fully-narrowed `colorBy`. Defaults to cascade while closed,
  // which only drives `disabledAttributes` (eval-scoped flags match across catalogs).
  const modalCatalog = useMemo<FilterCatalog | undefined>(() => {
    if (!catalogs) {
      return undefined;
    }
    return (modal?.mode === 'color' ? catalogs.colorBy : catalogs.cascade)[
      dataset
    ];
  }, [catalogs, dataset, modal?.mode]);

  // This dataset's applied categorical selections, by attribute — unioned into the
  // filter picker below so a value a filter above narrowed out stays deselectable.
  const selectedByAttribute = useMemo(() => {
    const map = new Map<string, string[]>();
    datasetFilters.forEach((filter) => {
      if (filter.selectedValues?.length) {
        map.set(filter.attribute, filter.selectedValues);
      }
    });
    return map;
  }, [datasetFilters]);

  // The modal's attribute list, derived from the mode's filter_options catalog.
  const attributes = useMemo<FilterAttributeOption[]>(() => {
    if (!modalCatalog) {
      return [];
    }
    return Object.entries(modalCatalog).map(([attribute, option]) => {
      // Map the BE eval-scoped flag to the modal's GENERIC disabled signal — the
      // modal never learns about eval-calc, it just renders the disabled row + hint.
      const disabled = isEvalScopedDisabled(option, hasCalc);
      return {
        attribute,
        label: attributeLabel(attribute),
        // Filter mode: keep this attribute's own selected values visible even if a
        // filter above narrowed them out, so they stay deselectable (the cascade never
        // offers a NEW value that would empty the set). Color mode passes through.
        option:
          modal?.mode === 'filter'
            ? withSelectedValuesVisible(
                option,
                selectedByAttribute.get(attribute)
              )
            : option,
        disabled,
        disabledReason: disabled ? EVAL_SCOPED_HINT : undefined,
        // Only the attributes whose facet values are stored tokens get relabelled;
        // everything else (operator, formation, basin…) renders verbatim.
        valueLabel: VALUE_LABELS[attribute] ?? catalogValueLabel(option),
      };
    });
  }, [modalCatalog, hasCalc, modal?.mode, selectedByAttribute]);

  // Attributes the modal renders disabled (eval-scoped in package mode). A disabled
  // attribute is skipped from the modal's `selections` (can't be edited), which would
  // otherwise make a pre-existing rule on it a `removed` tombstone — so an Apply while
  // viewing in package mode would silently drop a rule created in calc mode. Used below
  // to keep such rules out of the deletion set.
  const disabledAttributes = useMemo(
    () =>
      new Set(
        attributes.filter((item) => item.disabled).map((item) => item.attribute)
      ),
    [attributes]
  );

  // The modal opens pre-filled from this dataset's current rules for its mode (filter
  // cards or color rules), so the merge can update or remove them. Add opens in
  // natural order; Edit moves the clicked attribute first so the modal lands focused
  // on it (active = initialSelections[0]).
  const initialSelections = useMemo<FilterSelection[]>(() => {
    if (!modal) {
      return [];
    }
    const selections: FilterSelection[] =
      modal.mode === 'filter'
        ? datasetFilters.map((filter) => ({
            attribute: filter.attribute,
            kind: filter.kind,
            selectedValues: filter.selectedValues,
            range: filter.range,
          }))
        : colorRules.map((rule) => ({
            attribute: rule.attribute,
            kind: rule.kind,
            selectedValues: rule.selectedValues,
            range: rule.range,
            valueColors: rule.valueColors,
            rangeColor: rule.rangeColor,
            applyTo: rule.applyTo,
          }));
    if (!modal.focus) {
      return selections;
    }
    return [
      ...selections.filter((selection) => selection.attribute === modal.focus),
      ...selections.filter((selection) => selection.attribute !== modal.focus),
    ];
  }, [modal, datasetFilters, colorRules]);

  // Merge the modal's apply result onto the matching atom (scoped to this dataset)
  // and dispatch it as one atomic `set`. Filter ids are deterministic per
  // (dataset, attribute); color rules preserve their id + `enabled` on edit (the
  // existing entry rides through `mergeByAttribute`'s `toEntry`).
  const handleApply = useCallback(
    ({ selections, removed }: AttributeFilterApply) => {
      if (!modal) {
        return;
      }
      // Never delete a disabled attribute's rule via Apply — it lands in `removed`
      // only because the modal can't edit it (not because the user cleared it). Only
      // the card's × removes it. (Enabled-attribute removals pass through untouched.)
      const removedAttrs = removed.filter(
        (attribute) => !disabledAttributes.has(attribute)
      );
      if (modal.mode === 'filter') {
        const next = mergeByAttribute(
          allFilters,
          dataset,
          selections,
          removedAttrs,
          (selection): AppliedFilter => ({
            id: `${dataset}:${selection.attribute}`,
            dataset,
            attribute: selection.attribute,
            kind: selection.kind,
            selectedValues: selection.selectedValues,
            range: selection.range,
          })
        );
        dispatchFilters({ type: 'set', filters: next });
      } else {
        const next = mergeByAttribute(
          colorRules,
          dataset,
          selections,
          removedAttrs,
          (selection, existing): ColorRule => ({
            id: existing?.id ?? `${dataset}:${selection.attribute}`,
            dataset,
            attribute: selection.attribute,
            kind: selection.kind,
            selectedValues: selection.selectedValues,
            range: selection.range,
            valueColors: selection.valueColors,
            rangeColor: selection.rangeColor,
            applyTo: narrowApplyTo(selection.applyTo),
            // Preserve the enable state on edit; new rules start enabled.
            enabled: existing?.enabled ?? true,
          })
        );
        dispatchColors({ type: 'set', rules: next });
      }
      setModal(null);
    },
    [
      modal,
      allFilters,
      colorRules,
      dataset,
      disabledAttributes,
      dispatchFilters,
      dispatchColors,
    ]
  );

  const title = (
    <span className={styles.accordionTitle}>
      {DATASET_LABELS[dataset]}
      {count !== undefined && <> ({format0(count)})</>}
    </span>
  );

  return (
    <Accordion className={styles.accordion}>
      <AccordionSummary>{title}</AccordionSummary>
      <AccordionDetails>
        <div className={styles.filterSection}>
          <SectionActionButton
            status={catalogStatus}
            label="Add Filter"
            onOpen={() => setModal({ mode: 'filter', focus: null })}
            onRetry={() => refetchCatalog()}
            unavailableReason={catalogErrorObject?.message}
          />
          {datasetFilters.length === 0 ? (
            <p className={styles.emptyState}>No filters added.</p>
          ) : (
            <div className={styles.cards}>
              {datasetFilters.map((filter) => (
                <FilterCard
                  key={filter.id}
                  filter={filter}
                  label={attributeLabel(filter.attribute)}
                  onEdit={() =>
                    setModal({ mode: 'filter', focus: filter.attribute })
                  }
                  onRemove={() =>
                    dispatchFilters({ type: 'remove', id: filter.id })
                  }
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.colorSection}>
          <SectionActionButton
            status={catalogStatus}
            label="Color by Attribute"
            onOpen={() => setModal({ mode: 'color', focus: null })}
            onRetry={() => refetchCatalog()}
            unavailableReason={catalogErrorObject?.message}
          />
          {colorRules.length === 0 ? (
            <p className={styles.emptyState}>No color rules added.</p>
          ) : (
            <div className={styles.cards}>
              {legendColorRules.map((rule) => (
                <ColorRuleCard
                  key={rule.id}
                  rule={rule}
                  label={attributeLabel(rule.attribute)}
                  onEdit={() =>
                    setModal({ mode: 'color', focus: rule.attribute })
                  }
                  onRemove={() =>
                    dispatchColors({ type: 'remove', id: rule.id })
                  }
                  onEnabledChange={(enabled) =>
                    dispatchColors({
                      type: 'setEnabled',
                      id: rule.id,
                      enabled,
                    })
                  }
                  onValueColorChange={(value, color) =>
                    dispatchColors({
                      type: 'setValueColor',
                      id: rule.id,
                      value,
                      color,
                    })
                  }
                  onRangeColorChange={(color) =>
                    dispatchColors({
                      type: 'setRangeColor',
                      id: rule.id,
                      color,
                    })
                  }
                  colorStatus={colorStatuses[rule.id]?.status}
                  onRetry={colorStatuses[rule.id]?.onRetry}
                />
              ))}
            </div>
          )}
        </div>

        {modal && (
          <AttributeFilterModal
            title={
              modal.mode === 'color'
                ? `Color ${dataset} by attribute`
                : `Filter ${dataset}`
            }
            attributes={attributes}
            colorEnabled={modal.mode === 'color'}
            targets={modal.mode === 'color' ? COLOR_TARGETS : undefined}
            initialSelections={initialSelections}
            onApply={handleApply}
            onClose={() => setModal(null)}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
